import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CHATBOT_SEND_MESSAGE,
} from '../../../Constants/Config';
import {
  appendChatbotConversationMessages,
  buildConversationIdentityKey,
  clearChatbotConversation,
  loadChatbotConversation,
  normalizeChatbotEmailId,
  normalizeChatbotMerchantId,
  normalizeChatbotPageScope,
  saveChatbotConversation,
} from '../../../utils/chatbotConversationStorage';
import { invalidateChatbotEncryptionKeyCache } from '../../../utils/chatbotConversationCrypto';
import {
  buildChatbotApiUrl,
  getChatbotAuthFields,
  getChatbotMerchantName,
  getChatbotRequestHeaders,
} from '../../../Components/Chatbot/chatbotAuthFields';

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback;
  }
  return detail || error?.response?.data?.message || error?.message || fallback;
};

const persistConversationSnapshot = async ({
  merchantCode,
  emailId,
  pageScope,
  messages,
  activeThreadId,
}) => {
  const merchantId = normalizeChatbotMerchantId(merchantCode);
  const email = normalizeChatbotEmailId(emailId);
  const scope = normalizeChatbotPageScope(pageScope);
  if (!merchantId || !scope) return false;
  // Called from reducers: copy out of the Immer draft before the async save.
  const plainMessages = Array.isArray(messages)
    ? messages
        .filter((msg) => msg && !msg.pending)
        .map(({ id, role, content }) => ({ id, role, content }))
    : [];
  return saveChatbotConversation(merchantId, email, {
    messages: plainMessages,
    threadId: activeThreadId,
    pageScope: scope,
  });
};

const requestConversationKey = (arg) =>
  buildConversationIdentityKey(arg?.merchantId, arg?.emailId, arg?.pageScope);

const activeConversationKey = (state) =>
  buildConversationIdentityKey(
    state?.merchantCode,
    state?.emailId,
    state?.pageScope,
  );

const appendUniqueMessages = (current, incoming) => {
  const seen = new Set(current.map((msg) => msg.id));
  return [...current, ...incoming.filter((msg) => !seen.has(msg.id))];
};

/**
 * Decrypt + hydrate only for the live merchant + email + page scope.
 * Ignores stale results if the user/merchant/page changed mid-flight.
 */
export const hydrateChatbotConversation = createAsyncThunk(
  'chatbot/hydrateConversation',
  async ({ merchantId, emailId, pageScope }, { getState, rejectWithValue }) => {
    const merchant = normalizeChatbotMerchantId(merchantId);
    const email = normalizeChatbotEmailId(emailId);
    const scope = normalizeChatbotPageScope(pageScope);
    if (!merchant) {
      return rejectWithValue('Merchant is required.');
    }
    if (!scope) {
      return rejectWithValue('Page scope is required.');
    }

    try {
      const saved = await loadChatbotConversation(merchant, email, scope);
      const current = getState()?.chatbot;
      if (
        normalizeChatbotMerchantId(current?.merchantCode) !== merchant ||
        normalizeChatbotEmailId(current?.emailId) !== email ||
        normalizeChatbotPageScope(current?.pageScope) !== scope
      ) {
        return {
          merchantId: merchant,
          emailId: email,
          pageScope: scope,
          skipped: true,
        };
      }

      return {
        merchantId: merchant,
        emailId: email,
        pageScope: scope,
        skipped: false,
        threadId: saved?.threadId || null,
        messages: Array.isArray(saved?.messages) ? saved.messages : [],
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Unable to load conversation.');
    }
  },
);

/**
 * POST /python/chatbot — live chat.
 * Required: message, merchant_code, endpoint. Optional: thread_id, merchant_name.
 * Also sends token_id, login_type, session_id, merchant_id + Bearer.
 *
 * The request belongs to the page it was sent from. The exchange is written
 * to that page's stored conversation whether or not the user is still there,
 * and is only rendered if that page is on screen. A conversation that was
 * cleared (or a user who logged out) while waiting drops the reply.
 */
export const sendChatbotMessage = createAsyncThunk(
  'chatbot/sendMessage',
  async (
    {
      message,
      merchantId,
      emailId = '',
      endpoint,
      pageScope = '',
      threadId = null,
    },
    { getState, requestId, rejectWithValue },
  ) => {
    try {
      const normalizedMerchantId = normalizeChatbotMerchantId(merchantId);
      const normalizedEmailId = normalizeChatbotEmailId(
        emailId == null ? '' : emailId,
      );
      const normalizedEndpoint = String(endpoint || '').trim();

      if (!normalizedMerchantId) {
        return rejectWithValue('Merchant is required.');
      }
      if (!normalizedEndpoint) {
        return rejectWithValue('Page endpoint is required.');
      }

      const response = await axios.post(
        buildChatbotApiUrl(CHATBOT_SEND_MESSAGE),
        {
          message: message.trim(),
          merchant_code: normalizedMerchantId,
          merchant_name: getChatbotMerchantName(),
          endpoint: normalizedEndpoint,
          thread_id: threadId || null,
          ...getChatbotAuthFields(normalizedMerchantId),
        },
        { headers: getChatbotRequestHeaders() },
      );

      if (response.status >= 200 && response.status < 300 && response.data?.success) {
        const key = buildConversationIdentityKey(
          normalizedMerchantId,
          normalizedEmailId,
          pageScope,
        );
        if (getState()?.chatbot?.requests?.[key]?.requestId !== requestId) {
          return { discarded: true };
        }

        const replyThreadId = response.data?.thread_id || threadId || null;
        const exchange = [
          { id: `user-${requestId}`, role: 'user', content: message.trim() },
          ...(response.data?.message
            ? [
                {
                  id: `assistant-${requestId}`,
                  role: 'assistant',
                  content: response.data.message,
                },
              ]
            : []),
        ];

        if (normalizeChatbotPageScope(pageScope)) {
          await appendChatbotConversationMessages(
            normalizedMerchantId,
            normalizedEmailId,
            { pageScope, threadId: replyThreadId, messages: exchange },
          );
        }

        return { discarded: false, threadId: replyThreadId, messages: exchange };
      }

      return rejectWithValue(
        typeof response.data?.message === 'string'
          ? response.data.message
          : 'Unable to send message.',
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Unable to send message.'));
    }
  },
);

/**
 * Encrypt-persist current conversation, then keep panel closed.
 * isOpen clears in pending so route changes cannot remount an open panel.
 */
export const minimizeChatbot = createAsyncThunk(
  'chatbot/minimize',
  async (_, { getState }) => {
    const state = getState().chatbot;
    await persistConversationSnapshot({
      merchantCode: state.merchantCode,
      emailId: state.emailId,
      pageScope: state.pageScope,
      messages: state.messages,
      activeThreadId: state.activeThreadId,
    });
    return true;
  },
);

const initialState = {
  isOpen: false,
  merchantCode: null,
  emailId: '',
  endpoint: '',
  pageScope: '',
  activeThreadId: null,
  messages: [],
  hydrating: false,
  // Keyed by conversation identity (merchant + email + page scope):
  // { requestId, message, sentAt, status: 'pending' | 'failed', error }
  requests: {},
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    setChatbotContext: (state, { payload }) => {
      const nextMerchant = normalizeChatbotMerchantId(payload?.merchantCode);
      const nextEmail = normalizeChatbotEmailId(payload?.emailId);
      const nextEndpoint = String(payload?.endpoint || '').trim();
      const nextPageScope = normalizeChatbotPageScope(payload?.pageScope);
      const merchantChanged = state.merchantCode !== nextMerchant;
      const emailChanged = state.emailId !== nextEmail;
      const pageChanged =
        Boolean(nextPageScope) &&
        normalizeChatbotPageScope(state.pageScope) !== nextPageScope;

      if (nextEndpoint) {
        state.endpoint = nextEndpoint;
      }

      if (!merchantChanged && !emailChanged && !pageChanged) {
        if (nextMerchant) state.merchantCode = nextMerchant;
        state.emailId = nextEmail;
        if (nextPageScope) state.pageScope = nextPageScope;
        if (nextMerchant && nextPageScope && state.messages.length === 0) {
          state.hydrating = true;
        }
        return;
      }

      if (state.merchantCode && state.pageScope) {
        void persistConversationSnapshot({
          merchantCode: state.merchantCode,
          emailId: state.emailId,
          pageScope: state.pageScope,
          messages: state.messages,
          activeThreadId: state.activeThreadId,
        });
      }

      state.merchantCode = nextMerchant || null;
      state.emailId = nextEmail;
      state.pageScope = nextPageScope || '';
      state.activeThreadId = null;
      state.messages = [];
      state.hydrating = Boolean(nextMerchant && nextPageScope);
    },
    openChatbot: (state, { payload }) => {
      const merchant =
        normalizeChatbotMerchantId(payload?.merchantCode) || state.merchantCode;
      const email =
        payload?.emailId !== undefined
          ? normalizeChatbotEmailId(payload.emailId)
          : state.emailId;
      const endpoint = String(payload?.endpoint || state.endpoint || '').trim();
      const pageScope =
        payload?.pageScope !== undefined
          ? normalizeChatbotPageScope(payload.pageScope)
          : normalizeChatbotPageScope(state.pageScope);

      const merchantChanged = state.merchantCode !== merchant;
      const emailChanged = state.emailId !== email;
      const pageChanged =
        Boolean(pageScope) &&
        normalizeChatbotPageScope(state.pageScope) !== pageScope;

      if (endpoint) {
        state.endpoint = endpoint;
      }

      if (merchantChanged || emailChanged || pageChanged) {
        if (state.merchantCode && state.pageScope) {
          void persistConversationSnapshot({
            merchantCode: state.merchantCode,
            emailId: state.emailId,
            pageScope: state.pageScope,
            messages: state.messages,
            activeThreadId: state.activeThreadId,
          });
        }
        state.merchantCode = merchant || null;
        state.emailId = email;
        state.pageScope = pageScope || '';
        state.activeThreadId = null;
        state.messages = [];
        state.hydrating = Boolean(merchant && pageScope);
      } else if (merchant && pageScope && state.messages.length === 0) {
        state.hydrating = true;
      }

      state.isOpen = true;
    },
    closeAndClearChatbot: (state) => {
      const merchant = normalizeChatbotMerchantId(state.merchantCode);
      const email = normalizeChatbotEmailId(state.emailId);
      const pageScope = normalizeChatbotPageScope(state.pageScope);
      if (merchant && pageScope) {
        clearChatbotConversation(merchant, email, pageScope);
      }
      delete state.requests[activeConversationKey(state)];
      state.isOpen = false;
      state.activeThreadId = null;
      state.messages = [];
      state.hydrating = false;
    },
    clearChatbotError: (state) => {
      const key = activeConversationKey(state);
      if (state.requests[key]?.status === 'failed') {
        delete state.requests[key];
      }
    },
    rememberChatbotThread: (state, { payload }) => {
      const threadId = String(payload?.threadId || '').trim();
      if (threadId) state.activeThreadId = threadId;
    },
    appendChatbotLocalMessage: (state, { payload }) => {
      const content = String(payload?.content || '').trim();
      if (!content) return;
      state.messages.push({
        id: payload?.id || `local-${Date.now()}`,
        role: payload?.role === 'user' ? 'user' : 'assistant',
        content,
      });
      void persistConversationSnapshot({
        merchantCode: state.merchantCode,
        emailId: state.emailId,
        pageScope: state.pageScope,
        messages: state.messages,
        activeThreadId: state.activeThreadId,
      });
    },
    resetChatbot: () => {
      invalidateChatbotEncryptionKeyCache();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateChatbotConversation.pending, (state, { meta }) => {
        const merchant = normalizeChatbotMerchantId(meta?.arg?.merchantId);
        const email = normalizeChatbotEmailId(meta?.arg?.emailId);
        const pageScope = normalizeChatbotPageScope(meta?.arg?.pageScope);
        if (
          normalizeChatbotMerchantId(state.merchantCode) === merchant &&
          normalizeChatbotEmailId(state.emailId) === email &&
          normalizeChatbotPageScope(state.pageScope) === pageScope &&
          (state.hydrating || state.messages.length === 0)
        ) {
          state.hydrating = true;
        }
      })
      .addCase(hydrateChatbotConversation.fulfilled, (state, { payload }) => {
        if (!payload || payload.skipped) {
          state.hydrating = false;
          return;
        }
        if (
          normalizeChatbotMerchantId(state.merchantCode) !==
            payload.merchantId ||
          normalizeChatbotEmailId(state.emailId) !== payload.emailId ||
          normalizeChatbotPageScope(state.pageScope) !==
            normalizeChatbotPageScope(payload.pageScope)
        ) {
          return;
        }
        if (!state.hydrating && state.messages.length > 0) {
          return;
        }
        // Anything already in view arrived while the saved copy was loading
        // (a reply landing right as the user came back to this page).
        const saved = Array.isArray(payload.messages) ? payload.messages : [];
        state.messages = appendUniqueMessages(saved, state.messages);
        state.activeThreadId = payload.threadId || state.activeThreadId || null;
        state.hydrating = false;
      })
      .addCase(hydrateChatbotConversation.rejected, (state) => {
        state.hydrating = false;
      })
      .addCase(minimizeChatbot.pending, (state) => {
        state.isOpen = false;
      })
      .addCase(minimizeChatbot.rejected, (state) => {
        state.isOpen = false;
      })
      .addCase(sendChatbotMessage.pending, (state, { meta }) => {
        const key = requestConversationKey(meta?.arg);
        if (!key) return;
        state.requests[key] = {
          requestId: meta.requestId,
          message: String(meta?.arg?.message || '').trim(),
          sentAt: meta?.arg?.sentAt || null,
          status: 'pending',
          error: null,
        };
      })
      .addCase(sendChatbotMessage.fulfilled, (state, { payload, meta }) => {
        const key = requestConversationKey(meta?.arg);
        if (state.requests[key]?.requestId === meta.requestId) {
          delete state.requests[key];
        }
        if (!payload || payload.discarded) return;
        if (activeConversationKey(state) !== key) return;
        state.messages = appendUniqueMessages(state.messages, payload.messages);
        if (payload.threadId) state.activeThreadId = payload.threadId;
      })
      .addCase(sendChatbotMessage.rejected, (state, { payload, meta }) => {
        const key = requestConversationKey(meta?.arg);
        const request = state.requests[key];
        if (!request || request.requestId !== meta.requestId) return;
        state.requests[key] = {
          ...request,
          status: 'failed',
          error: payload || 'Unable to send message.',
        };
      });
  },
});

/** In-flight or failed request for the conversation currently on screen. */
export const selectActiveChatbotRequest = (rootState) => {
  const chatbot = rootState?.chatbot;
  return chatbot?.requests?.[activeConversationKey(chatbot)] || null;
};

export const {
  setChatbotContext,
  openChatbot,
  closeAndClearChatbot,
  clearChatbotError,
  rememberChatbotThread,
  appendChatbotLocalMessage,
  resetChatbot,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
